import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  makeStyles,
  Theme,
  Button,
} from '@bb-ui/react-library';
import { Clear, Upload, Globe } from '@bb-ui/icons/dist/small';
import { useTranslation } from 'react-i18next';
import { read as readXlsx, utils as xlsxUtils } from 'xlsx';
import { LearnInstanceItem } from './MassQueryTooling.types';
import { useSnackbar } from 'hooks/useSnackbar';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(4),
    position: 'relative',
  },
  layout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
  },
  listContainer: {
    flex: 2,
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  uploaderContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
  paper: {
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: 224,
    overflowY: 'auto',
    boxShadow: theme.shadows[1],
  },
  table: {
    '& th': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
    },
    '& td': {
      fontSize: '0.875rem',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  iconEmpty: {
    fontSize: 48,
    opacity: 0.5,
  },
  removeBtn: {
    color: theme.palette.error.main,
  },
  uploadButton: {
    minHeight: 48,
    borderRadius: theme.shape.borderRadius * 2,
    border: `2px dashed ${theme.palette.primary.main}`,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.light}10`,
      borderColor: theme.palette.primary.dark,
    },
  },
  hiddenInput: {
    display: 'none',
  },
}));

export interface MassQueryToolingSelectedSitesProps {
  selectedSites: LearnInstanceItem[];
  allSites: LearnInstanceItem[];
  onRemove: (tenantId: string) => void;
  onCsvImport: (tenantIds: string[]) => void;
  processFile?: (file: File) => void;
}

export const MassQueryToolingSelectedSites: React.FC<MassQueryToolingSelectedSitesProps> = ({
  selectedSites,
  allSites,
  onRemove,
  onCsvImport,
  processFile: processFileProp,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const findTenantIdColumn = (headers: string[]): number => {
    const normalized = headers.map((h) =>
      h
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, ''),
    );
    return normalized.indexOf('tenantid');
  };

  const parseCsvText = (text: string): string[] => {
    const lines = text
      .split(/\r?\n/)
      .map((r) => r.trim())
      .filter(Boolean);

    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map((h) => h.trim());
    const colIndex = findTenantIdColumn(headers);

    if (colIndex === -1) return lines.slice(1);

    return lines
      .slice(1)
      .map((line) => {
        const cols = line.split(',');
        return (cols[colIndex] ?? '').trim();
      })
      .filter(Boolean);
  };

  const parseExcelBuffer = (buffer: ArrayBuffer): string[] => {
    const workbook = readXlsx(buffer, { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows: string[][] = xlsxUtils.sheet_to_json(firstSheet, { header: 1 });

    if (rows.length === 0) return [];

    const headers = rows[0].map(String);
    const colIndex = findTenantIdColumn(headers);

    if (colIndex === -1) {
      return rows
        .slice(1)
        .map((row) => String(row[0] ?? '').trim())
        .filter(Boolean);
    }

    return rows
      .slice(1)
      .map((row) => String(row[colIndex] ?? '').trim())
      .filter(Boolean);
  };

  const processImportedIds = (tenantIds: string[]) => {
    const validIdsSet = new Set(allSites.map((s) => s.TenantId));
    const validTenantIds = tenantIds.filter((id) => validIdsSet.has(id));

    const currentIds = new Set(selectedSites.map((s) => s.TenantId));
    const newIds = validTenantIds.filter((id) => !currentIds.has(id));

    onCsvImport(validTenantIds);

    if (newIds.length > 0) {
      enqueueSnackbar(t('massQueryTooling.csv.success', { count: newIds.length }), {
        variant: 'success',
      });
    } else {
      enqueueSnackbar(t('massQueryTooling.csv.noNewEntries'), { variant: 'error' });
    }
  };

  const isExcelFile = (file: File): boolean => {
    const excelExtensions = ['.xlsx', '.xls'];
    return excelExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
  };

  const processFile = (file: File) => {
    const reader = new FileReader();

    if (isExcelFile(file)) {
      reader.onload = (ev) => {
        const buffer = ev.target?.result as ArrayBuffer;
        const tenantIds = parseExcelBuffer(buffer);
        processImportedIds(tenantIds);
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (ev) => {
        const text = String(ev.target?.result ?? '');
        const tenantIds = parseCsvText(text);
        processImportedIds(tenantIds);
      };
      reader.readAsText(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const handler = processFileProp || processFile;
    handler(file);
    e.target.value = '';
  };

  const handleClickUpload = () => {
    inputRef.current?.click();
  };

  return (
    <div className={classes.container}>
      <div className={classes.layout}>
        <div className={classes.listContainer}>
          <div className={classes.listHeader}>
            <Typography variant="subtitle1">{t('massQueryTooling.selectedSites.title')}</Typography>
            {selectedSites.length > 0 && (
              <Chip
                label={t('massQueryTooling.selectedSites.chipLabel', {
                  count: selectedSites.length,
                })}
                size="small"
                color="primary"
              />
            )}
          </div>

          {selectedSites.length === 0 ? (
            <Paper className={classes.paper}>
              <Box className={classes.emptyState}>
                <Globe className={classes.iconEmpty} />
                <Typography variant="body2">
                  {t('massQueryTooling.selectedSites.empty.title')}
                </Typography>
                <Typography variant="caption">
                  {t('massQueryTooling.selectedSites.empty.subtitle')}
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper className={classes.paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('massQueryTooling.selectedSites.headers.tenantId')}</TableCell>
                    <TableCell>{t('massQueryTooling.selectedSites.headers.url')}</TableCell>
                    <TableCell>{t('massQueryTooling.selectedSites.headers.remove')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSites.map((site) => {
                    // Look up URL from allSites if missing
                    const displayUrl =
                      site.Url || allSites.find((s) => s.TenantId === site.TenantId)?.Url || '';
                    return (
                      <TableRow key={site.TenantId}>
                        <TableCell>{site.TenantId}</TableCell>
                        <TableCell>{displayUrl}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => onRemove(site.TenantId)}
                            className={classes.removeBtn}
                            aria-label={t('massQueryTooling.selectedSites.actions.removeAriaLabel')}
                          >
                            <Clear />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </div>

        <div className={classes.uploaderContainer}>
          <div style={{ height: '25px' }} />
          <Button
            variant="text"
            className={classes.uploadButton}
            startIcon={<Upload />}
            onClick={handleClickUpload}
          >
            {t('massQueryTooling.selectedSites.actions.importFile')}
          </Button>

          <input
            ref={inputRef}
            type="file"
            className={classes.hiddenInput}
            accept=".csv,.xlsx,.xls"
            onChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  );
};
